import type Store from "@ember-data/store";
import type ModelRegistry from "@ember-data/types/registries/model";
import { assert } from "@ember/debug";
import Service, { service } from "@ember/service";
import type Medic from "parog-web/models/medic";
import type Patient from "parog-web/models/patient";

export enum Configuration {
  VitalSign = "configuration-vital-sign",
  Consultation = "configuration-consultation",
  BioExam = "configuration-bio-exam"
}

export type ConfigurationRegistry = Pick<ModelRegistry, Configuration>;

export default class ConfigurationService extends Service {
  static configurationIdForPatient(
    feature: keyof ConfigurationRegistry,
    patient: Patient
  ): string {
    return ConfigurationService.configurationId(feature, "patient", patient.id);
  }

  static configurationIdForMedic(
    feature: keyof ConfigurationRegistry,
    medic: Medic
  ): string {
    return ConfigurationService.configurationId(feature, "medic", medic.id);
  }

  static configurationId(
    feature: keyof ConfigurationRegistry,
    layer: "patient" | "medic",
    id: string | number
  ): string {
    return `${feature.replace("configuration-", "")}_${layer}-${id}`;
  }

  @service declare store: Store;

  async fetchConfigurationsForPatient(
    patient: Patient
  ): Promise<Array<unknown>> {
    const features = [
      Configuration.VitalSign,
      Configuration.Consultation,
      Configuration.BioExam
    ];

    return Promise.all(
      features.map(feature => {
        const configId = ConfigurationService.configurationIdForPatient(
          feature,
          patient
        );
        return this.store.findRecord(feature, configId);
      })
    );
  }

  async fetchConfigurationForMedic<T extends keyof ConfigurationRegistry>(
    feature: T,
    medic: Medic
  ): Promise<ConfigurationRegistry[T]> {
    const configId = ConfigurationService.configurationIdForMedic(
      feature,
      medic
    );
    return await this.store.findRecord(feature, configId);
  }

  configurationForPatient<K extends keyof ConfigurationRegistry>(
    feature: K,
    patient: Patient
  ): ConfigurationRegistry[K] {
    const record = this.store.peekRecord(
      feature,
      ConfigurationService.configurationIdForPatient(feature, patient)
    );
    assert(
      `No configuration found in store for patient ${patient.id}. This means the configuration has not been fetched first`,
      Boolean(record)
    );
    return record as ConfigurationRegistry[K];
  }

  configurationForMedic<K extends keyof ConfigurationRegistry>(
    feature: K,
    medic: Medic
  ): ConfigurationRegistry[K] {
    const record = this.store.peekRecord(
      feature,
      ConfigurationService.configurationIdForMedic(feature, medic)
    );
    assert(
      `No configuration found in store for medic ${medic.id}. This means the configuration has not been fetched first`,
      Boolean(record)
    );
    return record as ConfigurationRegistry[K];
  }
}

declare module "@ember/service" {
  interface Registry {
    configuration: ConfigurationService;
  }
}
